import { createI18n } from "vue-i18n";
import { adminPermissionDict } from "./locales/adminPermission";
import { adminRoleDict } from "./locales/adminRole";
import { adminUserDict } from "./locales/adminUser";
import { adminUserGroupDict } from "./locales/adminUserGroup";
import { authDict } from "./locales/auth";
import { bottleListingDict } from "./locales/bottleListing";
import { categoryDict } from "./locales/category";
import { commonDict } from "./locales/common";
import { dashboardDict } from "./locales/dashboard";
import { landingDict } from "./locales/landing";
import { locationDict } from "./locales/location";
import { menuDict } from "./locales/menu";
import { printLabelsDict } from "./locales/printLabels";
import { reportActivityLogDict } from "./locales/reportActivityLog";
import { reportAuditDict } from "./locales/reportAudit";
import { reportReceivingDict } from "./locales/reportReceiving";
import { reportStockOnHandDict } from "./locales/reportStockOnHand";
import { rfidTrackingDict } from "./locales/rfidTracking";
import { wineDict } from "./locales/wine";
import { wineImportDict } from "./locales/wineImport";
import { wineReceivingDict } from "./locales/wineReceiving";
import { systemConfigDict } from "./locales/systemConfig";
import { notificationDict } from "./locales/notification";

// i18n Dictionary — [English, ไทย]
// เพิ่ม/แก้ไขแค่ 1 บรรทัด ก็ครบทั้ง 2 ภาษา
// key ตั้งตามชื่อ Vue file เพื่อให้รู้ว่าเป็นของหน้าไหน
//
// Domain dicts live in ./locales/<domain>.ts. Spread order matters: dicts are
// spread first so any per-page key with the same path in the legacy block below
// loses (the dict value is canonical).

const dict: Record<string, [en: string, th: string]> = {
  ...adminPermissionDict,
  ...adminRoleDict,
  ...adminUserDict,
  ...adminUserGroupDict,
  ...authDict,
  ...bottleListingDict,
  ...categoryDict,
  ...commonDict,
  ...dashboardDict,
  ...landingDict,
  ...locationDict,
  ...menuDict,
  ...printLabelsDict,
  ...reportActivityLogDict,
  ...reportAuditDict,
  ...reportReceivingDict,
  ...reportStockOnHandDict,
  ...rfidTrackingDict,
  ...wineDict,
  ...wineImportDict,
  ...wineReceivingDict,
  ...systemConfigDict,
  ...notificationDict,

  // ── Per-view pageTitle / breadcrumb (no dedicated dict file) ────────
  // BottleStatusListing.vue
  "BottleStatusListing.pageTitle": ["Bottle Statuses", "สถานะขวด"],
  "BottleStatusListing.breadcrumb": ["Bottle Statuses", "สถานะขวด"],

  // CellarManagementManual.vue
  "CellarManagementManual.pageTitle": ["Management Manual", "คู่มือจัดการห้องเก็บไวน์"],
  "CellarManagementManual.breadcrumb": ["User Manual", "คู่มือการใช้งาน"],

  // WineListing.vue — page-level keys
  // (Shared wine-domain keys live in ./locales/wine.ts under `wine.*`)
  "WineListing.pageTitle": ["Wines", "ไวน์"],
  "WineListing.breadcrumb": ["Wines", "ไวน์"],

  // ReceiptItemListing.vue
  "ReceiptItemListing.pageTitle": ["Add Stock", "รับเข้าสต๊อก"],
  "ReceiptItemListing.breadcrumb": ["Add Stock", "รับเข้าสต๊อก"],

  // CompanyListing.vue
  "CompanyListing.pageTitle": ["Companies Management", "จัดการบริษัท"],

  // CompanyDetail.vue
  "CompanyDetail.pageTitle": ["Company Details", "รายละเอียดบริษัท"],

  // PackageListing.vue
  "PackageListing.pageTitle": ["Packages Management", "จัดการแพ็กเกจ"],
  "PackageListing.breadcrumb": ["Packages", "แพ็กเกจ"],

  // SubscriptionManagement.vue
  "SubscriptionManagement.pageTitle": ["Subscription Management", "การสมัครใช้งาน"],
  "SubscriptionManagement.breadcrumb": ["Subscriptions", "การสมัครใช้งาน"],

  // ── Pages with no page-level keys (all keys live in the dict files) ──
  // Dashboard.vue             — keys in ./locales/dashboard.ts
  // RfidTracking.vue          — keys in ./locales/rfidTracking.ts
  // StockOnHandReport.vue     — keys in ./locales/reportStockOnHand.ts
  // AuditReport.vue           — keys in ./locales/reportAudit.ts
  // ActivityLogsListing.vue   — keys in ./locales/reportActivityLog.ts
  // ReceivingReports.vue      — keys in ./locales/reportReceiving.ts
  // CategoryListing.vue       — keys in ./locales/category.ts
  // LocationListing.vue       — keys in ./locales/location.ts
  // BottleListing.vue         — keys in ./locales/bottleListing.ts
  // UserListing.vue           — keys in ./locales/adminUser.ts
  // UserDetail.vue            — keys in ./locales/adminUser.ts
  // RoleListing.vue           — keys in ./locales/adminRole.ts
  // RoleDetail.vue            — keys in ./locales/adminRole.ts
  // RoleAddModal.vue          — keys in ./locales/adminRole.ts
  // UserGroupListing.vue      — keys in ./locales/adminUserGroup.ts
};

// แปลง dict → vue-i18n messages
function buildMessages(src: Record<string, [string, string]>) {
  const en: Record<string, any> = {};
  const th: Record<string, any> = {};

  for (const [key, [eVal, tVal]] of Object.entries(src)) {
    const parts = key.split(".");
    let eCur = en, tCur = th;
    for (let i = 0; i < parts.length - 1; i++) {
      eCur = eCur[parts[i]] ??= {};
      tCur = tCur[parts[i]] ??= {};
    }
    eCur[parts.at(-1)!] = eVal;
    tCur[parts.at(-1)!] = tVal;
  }

  return { en, th };
}

const i18n = createI18n({
  legacy: false,
  locale:
    typeof window !== "undefined" ? localStorage.getItem("lang") || "en" : "en",
  fallbackLocale: "en",
  globalInjection: true,
  messages: buildMessages(dict),
});

export default i18n;
